import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { autoRoundOff, formatRoundOffLabel } from "../utils/autoRoundOff";
import { formatAmountInWords } from "../utils/numberToWords";
import {
  mergeProductGroupOptions,
  productGroupOptionDisplayName,
  urdProductGroupLabelForMetalType,
  resolveUrdProductGroupOption,
} from "../utils/productGroupOptions";
import OldPurchaseTable, {
  createDefaultOldPurchaseRow,
  parseNumericValue,
} from "./OldPurchaseTable";

const OldPurchaseModal = ({
  show = false,
  onClose,
  onSave,
  initialData = null,
  payMode = "old_gold",
  paymentRowId = null,
  masterGoldRate = 0,
  masterSilverRate = 0,
  showLiveRate = false,
}) => {
  const metalType = payMode === "old_silver" ? "OLD_SILVER" : "OLD_GOLD";
  const isSilverMode = metalType === "OLD_SILVER";

  const [tableRows, setTableRows] = useState(() => [createDefaultOldPurchaseRow(1)]);
  const [productGroupOptionsCache, setProductGroupOptionsCache] = useState([]);

  // Summary states
  const [gstPercentage, setGstPercentage] = useState("0");
  const [roundOffAmount, setRoundOffAmount] = useState("");
  const [isManualRoundOff, setIsManualRoundOff] = useState(false);
  const [note, setNote] = useState("");

  // Initialize or reset form state when modal opens
  useEffect(() => {
    if (!show) return;

    if (initialData && Array.isArray(initialData.items) && initialData.items.length > 0) {
      const mappedRows = initialData.items.map((it, idx) => ({
        id: idx + 1,
        product_group_id: it.product_group_id ? String(it.product_group_id) : "",
        product_group_name: it.product_group_name || it.name || "",
        pcs: it.pcs !== undefined && it.pcs !== null ? String(it.pcs) : "",
        ct: it.ct !== undefined && it.ct !== null ? String(it.ct) : "",
        gwt: it.gwt !== undefined && it.gwt !== null ? String(it.gwt) : "",
        lwt: it.lwt !== undefined && it.lwt !== null ? String(it.lwt) : "",
        nwt: it.nwt !== undefined && it.nwt !== null ? String(it.nwt) : "",
        touch: it.touch !== undefined && it.touch !== null ? String(it.touch) : "",
        fine_wt: it.fine_wt !== undefined && it.fine_wt !== null ? String(it.fine_wt) : "",
        total_amt: it.total_amt !== undefined && it.total_amt !== null ? String(it.total_amt) : "",
        isTotalAmtManual: it.isTotalAmtManual || false,
      }));

      setTableRows(mappedRows);
      setGstPercentage(initialData.gst_percent !== undefined ? String(initialData.gst_percent) : "0");
      if (initialData.round_off !== undefined && initialData.round_off !== null) {
        setRoundOffAmount(String(initialData.round_off));
        setIsManualRoundOff(true);
      } else {
        setRoundOffAmount("");
        setIsManualRoundOff(false);
      }
      setNote(initialData.notes || initialData.note || "");
    } else {
      // New URD purchase entry: show Old Gold / Old Silver immediately, then bind the real id
      const displayName = urdProductGroupLabelForMetalType(metalType);
      const firstRow = {
        ...createDefaultOldPurchaseRow(1),
        product_group_name: displayName,
      };
      setTableRows([firstRow]);
      setGstPercentage("0");
      setRoundOffAmount("");
      setIsManualRoundOff(false);
      setNote("");

      let cancelled = false;
      (async () => {
        try {
          const pgOption = await resolveUrdProductGroupOption(metalType);
          if (cancelled || !pgOption) return;

          const defaultPg = pgOption.value ?? pgOption.id;
          const label = productGroupOptionDisplayName(pgOption) || displayName || "";
          setProductGroupOptionsCache((prev) => mergeProductGroupOptions(prev, [pgOption]));
          setTableRows((prev) =>
            prev.map((row, idx) =>
              idx === 0 && !row.product_group_id
                ? {
                  ...row,
                  product_group_id: String(defaultPg),
                  product_group_name: row.product_group_name || label,
                }
                : row,
            ),
          );
        } catch (e) {
          console.error("Error setting default product group in URD modal:", e);
        }
      })();

      return () => {
        cancelled = true;
      };
    }
  }, [show, initialData, metalType, isSilverMode]);

  // Totals calculations
  const totalAmount = useMemo(() => {
    return tableRows.reduce((sum, row) => sum + parseNumericValue(row.total_amt), 0);
  }, [tableRows]);

  const totalNwt = useMemo(() => {
    return tableRows.reduce((sum, row) => sum + parseNumericValue(row.nwt), 0);
  }, [tableRows]);

  // GST calculations
  const gstAmount = useMemo(() => {
    const rate = parseFloat(gstPercentage);
    if (!rate || isNaN(rate) || rate <= 0) return 0;
    return Number(((totalAmount * rate) / 100).toFixed(2));
  }, [totalAmount, gstPercentage]);

  const amountBeforeRoundOff = totalAmount + gstAmount;

  const autoPaymentRoundOff = useMemo(() => {
    return autoRoundOff(amountBeforeRoundOff).roundOff;
  }, [amountBeforeRoundOff]);

  const effectiveRoundOff = isManualRoundOff
    ? parseFloat(roundOffAmount) || 0
    : autoPaymentRoundOff;

  const displayRoundOff = isManualRoundOff
    ? roundOffAmount
    : autoPaymentRoundOff.toFixed(2);

  const grandTotal = Math.max(0, amountBeforeRoundOff + effectiveRoundOff);
  const remainingAmount = grandTotal;

  const roundOffLabel = useMemo(() => {
    const label = formatRoundOffLabel(effectiveRoundOff);
    if (label === "ROUND UP") return "Round Up";
    if (label === "ROUND DOWN") return "Round Down";
    return "Round Off";
  }, [effectiveRoundOff]);

  const finalAmountInWords = useMemo(() => {
    return formatAmountInWords(grandTotal.toFixed(2));
  }, [grandTotal]);

  const handleRoundOffChange = (e) => {
    const val = e.target.value;
    if (val === "" || /^-?\d*\.?\d*$/.test(val)) {
      setRoundOffAmount(val);
      setIsManualRoundOff(true);
    }
  };

  const handleSaveModal = async () => {
    let rows = tableRows;
    const needsProductGroupId = rows.some(
      (r) =>
        !r.product_group_id &&
        (parseNumericValue(r.gwt) > 0 || parseNumericValue(r.nwt) > 0),
    );

    if (needsProductGroupId) {
      try {
        const pgOption = await resolveUrdProductGroupOption(metalType);
        const defaultPg = pgOption?.value ?? pgOption?.id;
        if (defaultPg != null) {
          const label = productGroupOptionDisplayName(pgOption) || "";
          rows = rows.map((row) =>
            row.product_group_id
              ? row
              : {
                ...row,
                product_group_id: String(defaultPg),
                product_group_name: row.product_group_name || label,
              },
          );
          setTableRows(rows);
          setProductGroupOptionsCache((prev) =>
            mergeProductGroupOptions(prev, [pgOption]),
          );
        }
      } catch (e) {
        console.error("Error resolving product group on URD save:", e);
      }
    }

    const validRows = rows.filter((r) => {
      const gwt = parseNumericValue(r.gwt);
      const nwt = parseNumericValue(r.nwt);
      return Boolean(r.product_group_id || r.product_group_name) && (gwt > 0 || nwt > 0);
    });

    if (validRows.length === 0) {
      toast.error("Please add at least one item with Product Group and Weight");
      return;
    }

    const payloadData = {
      metal_type: metalType,
      pay_mode: payMode,
      paymentRowId: paymentRowId,
      items: rows
        .filter((r) => parseNumericValue(r.gwt) > 0 || parseNumericValue(r.nwt) > 0 || Boolean(r.product_group_id))
        .map((r) => ({
          product_group_id: r.product_group_id ? Number(r.product_group_id) || r.product_group_id : null,
          product_group_name: r.product_group_name || "",
          pcs: parseInt(r.pcs, 10) || 0,
          ct: parseNumericValue(r.ct),
          gwt: parseNumericValue(r.gwt),
          lwt: parseNumericValue(r.lwt),
          nwt: parseNumericValue(r.nwt),
          touch: parseNumericValue(r.touch),
          fine_wt: parseNumericValue(r.fine_wt),
          total_amt: parseNumericValue(r.total_amt),
          isTotalAmtManual: Boolean(r.isTotalAmtManual),
        })),
      total_amount: Number(totalAmount.toFixed(2)),
      total_nwt: Number(totalNwt.toFixed(3)),
      gst_percent: parseFloat(gstPercentage) || 0,
      gst_amount: gstAmount,
      round_off: Number(effectiveRoundOff.toFixed(2)),
      grand_total: Number(grandTotal.toFixed(2)),
      notes: note?.trim() || "",
    };

    onSave?.(payloadData);
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      centered
      backdrop="static"
      dialogClassName="modal-95w"
    >
      <Modal.Header closeButton className="border-bottom py-2 px-3">
        <Modal.Title style={{ fontSize: "1.1rem", fontWeight: "600" }}>
          {isSilverMode ? "Old Silver URD Purchase" : "Old Gold URD Purchase"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-3" style={{ maxHeight: "calc(88vh - 120px)", overflowY: "auto" }}>
        {/* Reusable Items Table */}
        <OldPurchaseTable
          key={show ? `${payMode}-open` : "closed"}
          tableRows={tableRows}
          setTableRows={setTableRows}
          productGroupOptionsCache={productGroupOptionsCache}
          setProductGroupOptionsCache={setProductGroupOptionsCache}
          masterGoldRate={masterGoldRate}
          masterSilverRate={masterSilverRate}
          showLiveRate={showLiveRate}
          isSilverMode={isSilverMode}
          portalZIndex={10060}
          defaultProductGroupLabel={isSilverMode ? "Old Silver" : "Old Gold"}
          tableClass="table-hover theme-header-table"
        />

        {/* Bottom Summary Section */}
        <div className="mt-1">
          {/* Final Amt in words line */}
          <div className="mb-2" style={{ fontSize: "0.85rem", lineHeight: 1.4 }}>
            <span className="fw-semibold">Final Amt:</span> {grandTotal.toFixed(2)}{" "}
            <span className="text-muted fst-italic">({finalAmountInWords})</span>
          </div>

          <div className="d-flex gap-3 align-items-start">
            {/* Summary Table */}
            <div className="table-responsive border rounded" style={{ minWidth: "380px" }}>
              <table className="table table-bordered table-sm m-0" style={{ fontSize: "0.85rem" }}>
                <colgroup>
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                </colgroup>
                <tbody>
                  {/* Row 1: GST % & GST Amt */}
                  <tr>
                    <td className="p-0 align-middle fw-semibold ps-2" style={{ height: "34px" }}>
                      GST %
                    </td>
                    <td className="p-0 align-middle" style={{ height: "34px" }}>
                      <input
                        type="number"
                        className="form-control form-control-sm border-0 rounded-0 shadow-none px-2"
                        placeholder="0.00"
                        value={gstPercentage}
                        onChange={(e) => setGstPercentage(e.target.value)}
                        style={{ height: "34px", fontSize: "0.85rem" }}
                      />
                    </td>
                    <td className="p-0 align-middle fw-semibold ps-2" style={{ height: "34px" }}>
                      GST Amt
                    </td>
                    <td className="p-0 align-middle" style={{ height: "34px" }}>
                      <input
                        type="text"
                        className="form-control form-control-sm border-0 rounded-0 shadow-none px-2 bg-light text-end"
                        readOnly
                        value={gstAmount.toFixed(2)}
                        style={{ height: "34px", fontSize: "0.85rem" }}
                      />
                    </td>
                  </tr>

                  {/* Row 2: Round Off & Total */}
                  <tr>
                    <td className="p-0 align-middle fw-semibold ps-2" style={{ height: "34px" }}>
                      {roundOffLabel}
                    </td>
                    <td className="p-0 align-middle" style={{ height: "34px" }}>
                      <input
                        type="text"
                        className="form-control form-control-sm border-0 rounded-0 shadow-none px-2 text-end"
                        value={displayRoundOff}
                        onChange={handleRoundOffChange}
                        placeholder="0.00"
                        style={{ height: "34px", fontSize: "0.85rem" }}
                      />
                    </td>
                    <td
                      className="p-0 align-middle fw-bold ps-2 text-success"
                      style={{ height: "34px", backgroundColor: "#e8f8f0" }}
                    >
                      Total
                    </td>
                    <td className="p-0 align-middle" style={{ height: "34px", backgroundColor: "#e8f8f0" }}>
                      <input
                        type="text"
                        className="form-control form-control-sm border-0 rounded-0 shadow-none px-2 fw-bold text-success text-end"
                        readOnly
                        value={grandTotal.toFixed(2)}
                        style={{ height: "34px", fontSize: "0.85rem", backgroundColor: "transparent" }}
                      />
                    </td>
                  </tr>

                  {/* Row 3: Remaining Amount */}
                  <tr>
                    <td className="p-0 border-0" colSpan={2}></td>
                    <td
                      className="p-0 align-middle fw-bold ps-2 text-danger"
                      style={{ height: "34px", backgroundColor: "#fdeeed" }}
                    >
                      Remaining Amount
                    </td>
                    <td className="p-0 align-middle" style={{ height: "34px", backgroundColor: "#fdeeed" }}>
                      <input
                        type="text"
                        className="form-control form-control-sm border-0 rounded-0 shadow-none px-2 fw-bold text-danger text-end"
                        readOnly
                        value={remainingAmount.toFixed(2)}
                        style={{ height: "34px", fontSize: "0.85rem", backgroundColor: "transparent" }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Note Area — right side of the table */}
            <div style={{ flex: 1 }}>
              <label className="form-label fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>
                Note:
              </label>
              <div className="position-relative">
                <textarea
                  className="form-control shadow-none"
                  rows={3}
                  maxLength={70}
                  placeholder="Enter note"
                  value={note}
                  onChange={(e) => {
                    if (e.target.value.length <= 70) {
                      setNote(e.target.value);
                    }
                  }}
                  style={{ fontSize: "0.85rem", paddingBottom: "1.5rem" }}
                />
                <span
                  className="position-absolute text-muted"
                  style={{
                    fontSize: "0.75rem",
                    bottom: "4px",
                    right: "8px",
                    pointerEvents: "none",
                  }}
                >
                  {note.length}/70
                </span>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="py-2 px-3 border-top d-flex justify-content-end gap-2">
        <Button variant="primary" size="sm" onClick={handleSaveModal}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OldPurchaseModal;
